import { getChannelClient, getChannelHost } from 'src/utils/messages/singleton'
import { registerModal } from 'src/utils/messages/modalChannel'
import { renderModal } from './dialog'

// Cross-frame modal dispatcher: pick the right runtime path based on
// what surrounds the calling iframe, then either render the modal
// here or hand it off to the parent frame to render.
//
//   1. parent === self (standalone widget URL loaded in a top-level
//      tab, not embedded) — there is no parent; renderModal directly
//      in the current document.
//   2. channel handshake succeeds (the parent loaded our embed script
//      and is running Widgets.js#init with its openModal listener
//      registered) — send the openModal channel message; the parent
//      renders the dialog over the full host page.
//   3. channel handshake times out / rejects (parent didn't load our
//      embed script — "improperly embedded") — fall back to rendering
//      in this iframe. Visually cramped due to iframe sizing
//      constraints but the donor still sees the dialog and can act.
//
// All three paths land on the same renderModal({ src, ariaLabel,
// height }) contract; the choice of which frame hosts the dialog is
// invisible to the caller.
//
// Note: openModal is the generalized API. The existing
// openDonationModal channel message stays as the donate-widget-
// specific path because its payload shape feeds createWidget's
// pipeline (httpsOnly fallback, locale detection, channel
// registration, height sync) rather than a plain iframe.
//
// `src` is resolved to an absolute URL against this frame's origin
// before any path runs. The browser does this automatically for an
// anchor's .href (so openModalTriggers' anchor form has always sent
// absolute URLs), but `data-plg-modal-src` on a button is returned
// verbatim by dataset, which means a "/widgets/foo" path delivered
// over the channel would be resolved against the *receiver's*
// origin (FLP / partner page), not pledge.to, and 404. Doing the
// resolution at the public boundary catches every caller —
// triggers, future callers, channel and local paths alike — without
// any caller having to remember to use the `_url` Rails helper.
export const openModal = async opts => {
  opts = absolutifyOptsSrc(opts)
  if (parent === self) return renderLocal(opts)

  const client = getChannelClient()
  try {
    await client.ready
    client.send('openModal', opts)
  } catch {
    renderLocal(opts)
  }
}

// Resolve opts.src to an absolute URL against the current frame's
// origin. No-op when src is already absolute (URL parsing ignores
// the base in that case) or absent. Falls through with the original
// opts on parse failure so renderModal's allowlist can warn and
// reject rather than openModal throwing.
//
// Exported so the openPortalModal sibling can reuse the same logic —
// both modules send `src` over the channel where a relative path
// would otherwise resolve against the receiver's (partner page's)
// origin and 404.
export const absolutifyOptsSrc = opts => {
  if (!opts?.src) return opts
  try {
    return { ...opts, src: new URL(opts.src, location.origin).href }
  } catch {
    return opts
  }
}

// Local-render branch for openModal — used for both `parent === self`
// (top frame, no parent to delegate to) and the channel-failed
// fallback. Consults the same registry dispatchOpenModal does, so a
// donate widget that's registered (say) `share:*` to open its own
// inline BS5 modal in mode 1 wins over the default `renderModal`
// iframe. If nothing is registered, render the iframe as before and
// wire the resulting iframe + dialog into the local channel host +
// modalChannel registry so the modal iframe's CONNECT handshake is
// accepted and the opener-channel forwarder can find the dialog on
// 'close'. Opener is null because the opener IS this window — no
// separate iframe to forward inner messages to.
const renderLocal = opts => {
  const handler = opts?.id && findOpenModalHandler(opts.id)
  if (handler) return handler(opts)

  const result = renderModal(opts)
  if (result) {
    getChannelHost({ origin: location.origin }).register(result.iframe)
    registerModal({ ...result, opener: null })
  }
  return result
}

// Layered interception. A frame that wants to intercept modal opens
// by id registers a handler here; whichever frame's channel host
// receives the `openModal` envelope first runs dispatchOpenModal
// (below), which checks the registry, runs the matching handler if
// any, otherwise bubbles the message up to its own parent — or, at
// the top frame, falls through to the caller-supplied default
// (typically renderModal).
//
// Patterns:
//   - exact:    'multi-org:pi-abc123' → only that id
//   - wildcard: 'share:*' → any id starting with 'share:'
//   - global:   '*' → every id
// Wildcards exist so a level can intercept a whole type without
// needing to know each instance's key — e.g. an FLP registers
// `share:*` and handles every per-partner-item share modal a donate
// widget on the page might trigger.
//
// Registration returns a teardown that removes the handler. First
// match wins when multiple handlers match an id; register more
// specific patterns first if you depend on ordering.
//
// The registry is stashed on window so multiple bundles in the same
// frame share one list — same pattern getChannelHost / getChannelClient
// use. On an FLP page the embed-script bundle (Widgets.js#init) calls
// dispatchOpenModal while the flp/app.js bundle calls
// registerOpenModal — each bundle has its own module-scope copy of
// this file, so without the window stash the two would write to
// different arrays and the FLP registration would be invisible to
// the dispatcher.
const HANDLERS_KEY = '__plgOpenModalHandlers'
const openModalHandlers = () =>
  (window[HANDLERS_KEY] ||= [])

const makeMatcher = pattern => {
  if (pattern === '*') return () => true
  if (pattern.endsWith(':*')) {
    const prefix = pattern.slice(0, -1)
    return id => typeof id === 'string' && id.startsWith(prefix)
  }
  return id => id === pattern
}

export const registerOpenModal = (pattern, handler) => {
  const handlers = openModalHandlers()
  const entry = { matcher: makeMatcher(pattern), handler }
  handlers.push(entry)
  return () => {
    const i = handlers.indexOf(entry)
    if (i >= 0) handlers.splice(i, 1)
  }
}

// First matching handler for an id, or undefined. Shared by both
// the sender-side renderLocal branch (openModal) and the receiver-
// side dispatchOpenModal so a registration fires the same way
// whichever entry point picks it up.
const findOpenModalHandler = id =>
  openModalHandlers().find(h => h.matcher(id))?.handler

// Receiver-side helper. Each level's `host.onAll('openModal', ...)`
// listener delegates to this. Three outcomes:
//
//   - A registered handler matches `opts.id` → run it; we're done.
//   - No handler, but we have a parent → bubble the same envelope up
//     via our channel client so the parent level can take a crack at
//     it. Multi-level interception falls out naturally: intermediate
//     levels with no registration just forward, terminal levels
//     either handle or hit `onUnhandled`.
//   - No handler and no parent → call `onUnhandled(opts)`. Caller
//     decides the default — typically renderModal, possibly wrapped
//     to register the iframe with the local host for height sync.
export const dispatchOpenModal = (opts, { onUnhandled }) => {
  const handler = opts?.id && findOpenModalHandler(opts.id)
  if (handler) return handler(opts)
  if (parent !== self) return getChannelClient().send('openModal', opts)
  return onUnhandled(opts)
}

// Test-only — clears registered handlers so specs start clean.
export const __resetOpenModalHandlersForTests = () => {
  delete window[HANDLERS_KEY]
}
